/**
 * SetNextAVTransportURI gapless handoff — capability-gated, ON by default for the fleet.
 * Per-renderer override: gaplessPlaybackSettings (Supabase + local cache).
 * Fleet rollback override: localStorage 'gapless_setnext' === '0' forces off globally.
 */

import {
  getRendererGaplessEnabled,
  hasRendererGaplessPref,
} from '../services/gaplessPlaybackSettings';

export type SetNextZoneHint = {
  id?: string;
  name?: string;
  type?: string;
  metadata?: Record<string, unknown>;
};

/** Global fleet flag; localStorage 'gapless_setnext' === '0' forces off, '1' forces on. */
export function isGaplessSetNextFleetEnabled(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const v = localStorage.getItem('gapless_setnext');
    if (v === '0') return false;
    if (v === '1') return true;
    return true;
  } catch {
    return true;
  }
}

/** @deprecated Use isGaplessSetNextFleetEnabled — kept for call-site clarity in transport gates. */
export function isGaplessSetNextEnabled(): boolean {
  return isGaplessSetNextFleetEnabled();
}

export type SetNextCapableRenderer = {
  id: string;
  name?: string;
  avt_supports_set_next?: boolean;
  gapless_enabled?: boolean;
};

function resolveRendererGaplessEnabled(
  renderer: SetNextCapableRenderer | null | undefined,
  zone?: SetNextZoneHint | null,
): boolean {
  const rendererId = renderer?.id || zone?.id;
  if (rendererId && hasRendererGaplessPref(rendererId)) {
    return getRendererGaplessEnabled(rendererId);
  }
  if (renderer?.gapless_enabled === false) return false;
  if (zone?.metadata?.gapless_enabled === false) return false;
  return true;
}

/** True when AVTransport SCPD actionList advertises SetNextAVTransportURI (discovery-time). */
export function rendererSupportsSetNext(
  renderer: SetNextCapableRenderer | null | undefined,
  zone?: SetNextZoneHint | null,
): boolean {
  if (renderer?.avt_supports_set_next === true) return true;
  if (zone?.metadata?.avt_supports_set_next === true) return true;
  return false;
}

export function connectorRendererFromZone(zone: {
  id: string;
  name?: string;
  metadata?: Record<string, unknown>;
}): SetNextCapableRenderer {
  const gapless_enabled = resolveRendererGaplessEnabled(
    { id: zone.id, gapless_enabled: zone.metadata?.gapless_enabled === false ? false : undefined },
    zone,
  );
  return {
    id: zone.id,
    name: zone.name,
    avt_supports_set_next: zone.metadata?.avt_supports_set_next === true,
    gapless_enabled,
  };
}

/**
 * Flag ON + SetNext-capable: call setNextUri for prefetch even on timer-based renderers.
 * Gated purely on avt_supports_set_next (SCPD capability) — no transport/manufacturer heuristics.
 */
export function shouldAttemptSetNextPrefetch(
  renderer: SetNextCapableRenderer | null | undefined,
  zone?: SetNextZoneHint | null,
): boolean {
  if (!isGaplessSetNextFleetEnabled()) return false;
  if (!renderer?.id) return false;
  if (!resolveRendererGaplessEnabled(renderer, zone)) return false;
  return rendererSupportsSetNext(renderer, zone);
}
